import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ImagePlus, Star } from "lucide-react";
import { SEO } from "@/components/SEO";
import { fetchArticles, createArticle, updateArticle, coverImageSrc } from "@/lib/api";

function getErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return "Something went wrong saving that article.";
}

const today = () => new Date().toISOString().slice(0, 10);

export default function AdminArticleForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [loadingArticle, setLoadingArticle] = useState(isEdit);
  const [title, setTitle] = useState("");
  const [authorName, setAuthorName] = useState("Lara Parrish");
  const [date, setDate] = useState(today());
  const [body, setBody] = useState("");
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [existingCoverUrl, setExistingCoverUrl] = useState(null);
  const [coverImageFocalY, setCoverImageFocalY] = useState(50);
  const [isFeatured, setIsFeatured] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const all = await fetchArticles();
        const article = all.find((a) => a.id === id);
        if (!article) {
          toast.error("Article not found.");
          navigate("/admin");
          return;
        }
        setTitle(article.title);
        setAuthorName(article.author_name);
        setDate(article.date);
        setBody(article.body);
        setExistingCoverUrl(coverImageSrc(article));
        setCoverImageFocalY(article.cover_image_focal_y ?? 50);
        setIsFeatured(Boolean(article.is_featured));
      } catch {
        toast.error("Couldn't load that article.");
        navigate("/admin");
      } finally {
        setLoadingArticle(false);
      }
    })();
  }, [id, isEdit, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const fields = { title, authorName, date, body, coverImageFile, isFeatured, coverImageFocalY };
      if (isEdit) {
        await updateArticle(id, fields);
        toast.success("Article updated.");
      } else {
        await createArticle(fields);
        toast.success("Article published.");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const previewUrl = coverImageFile ? URL.createObjectURL(coverImageFile) : existingCoverUrl;

  if (loadingArticle) {
    return (
      <div className="flex items-center gap-2 text-[#57534E] py-16">
        <Loader2 className="animate-spin" size={18} /> Loading article…
      </div>
    );
  }

  return (
    <div data-testid="admin-article-form-page" className="max-w-2xl">
      <SEO title={isEdit ? "Edit Article" : "New Article"} description="Blueprint4Joy® admin." path="/admin/articles" noindex />
      <Link to="/admin" data-testid="admin-form-back-link" className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-[#159E9A] mb-6 transition">
        <ArrowLeft size={14} /> Back to articles
      </Link>
      <h1 className="font-display text-3xl text-[#1C1917] mb-8">
        {isEdit ? "Edit Article" : "New Article"}
      </h1>

      <form onSubmit={onSubmit} data-testid="admin-article-form" className="space-y-6">
        <div>
          <label className="block text-xs tracking-wide uppercase text-[#57534E] mb-1.5">Title</label>
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="A field note from the week"
            data-testid="admin-article-title"
            className="w-full rounded-xl px-4 py-3 text-base bg-white border border-[#E7E5E4] outline-none focus:border-[#159E9A] transition font-display"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs tracking-wide uppercase text-[#57534E] mb-1.5">Author</label>
            <input
              required
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              data-testid="admin-article-author"
              className="w-full rounded-xl px-4 py-3 text-sm bg-white border border-[#E7E5E4] outline-none focus:border-[#159E9A] transition"
            />
          </div>
          <div>
            <label className="block text-xs tracking-wide uppercase text-[#57534E] mb-1.5">Date</label>
            <input
              required
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="admin-article-date"
              className="w-full rounded-xl px-4 py-3 text-sm bg-white border border-[#E7E5E4] outline-none focus:border-[#159E9A] transition"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs tracking-wide uppercase text-[#57534E] mb-1.5">Cover Image</label>
          <label
            htmlFor="admin-cover-upload"
            data-testid="admin-article-cover-dropzone"
            className="flex items-center gap-4 border border-dashed border-[#E7E5E4] rounded-xl p-4 bg-white cursor-pointer hover:border-[#159E9A] transition"
          >
            {previewUrl ? (
              <img src={previewUrl} alt="Cover preview" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" data-testid="admin-article-cover-preview" />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-[#F5F4F1] flex items-center justify-center flex-shrink-0 text-[#57534E]">
                <ImagePlus size={22} />
              </div>
            )}
            <span className="text-sm text-[#57534E]">
              {coverImageFile ? coverImageFile.name : "Click to upload a cover image"}
            </span>
          </label>
          <input
            id="admin-cover-upload"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setCoverImageFile(file);
              if (file) setCoverImageFocalY(50);
            }}
            data-testid="admin-article-cover-input"
            className="hidden"
          />
        </div>

        {previewUrl && (
          <div data-testid="admin-cover-focal-point-section">
            <label className="block text-xs tracking-wide uppercase text-[#57534E] mb-1.5">
              Photo Position <span className="text-[#B8B2A7]">(keep her face inside both previews)</span>
            </label>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p className="text-[10px] tracking-wide uppercase text-[#B8B2A7] mb-1">Article list card</p>
                <div className="relative overflow-hidden rounded-lg bg-[#F1EFEA] aspect-[4/3]">
                  <img
                    src={previewUrl}
                    alt="Card preview"
                    data-testid="admin-cover-preview-card"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: `center ${coverImageFocalY}%` }}
                  />
                </div>
              </div>
              <div>
                <p className="text-[10px] tracking-wide uppercase text-[#B8B2A7] mb-1">Article banner (feature photo)</p>
                <div className="relative overflow-hidden rounded-lg bg-[#F1EFEA] aspect-[4/5]">
                  <img
                    src={previewUrl}
                    alt="Banner preview"
                    data-testid="admin-cover-preview-banner"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: `center ${coverImageFocalY}%` }}
                  />
                </div>
              </div>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={coverImageFocalY}
              onChange={(e) => setCoverImageFocalY(Number(e.target.value))}
              data-testid="admin-article-focal-slider"
              className="w-full accent-[#FF5A4F]"
            />
            <div className="flex justify-between text-[10px] tracking-wide uppercase text-[#B8B2A7] mt-1">
              <span>Show top of photo</span>
              <span>Show bottom of photo</span>
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs tracking-wide uppercase text-[#57534E] mb-1.5">Body</label>
          <textarea
            required
            rows={14}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your article here. Leave a blank line between paragraphs."
            data-testid="admin-article-body"
            className="w-full rounded-xl px-4 py-3 text-sm bg-white border border-[#E7E5E4] outline-none focus:border-[#159E9A] transition leading-relaxed"
          />
        </div>

        <label
          htmlFor="admin-article-featured"
          data-testid="admin-article-featured-label"
          className="flex items-center gap-3 rounded-xl border border-[#E7E5E4] bg-white p-4 cursor-pointer hover:border-[#159E9A] transition w-fit"
        >
          <input
            id="admin-article-featured"
            type="checkbox"
            checked={isFeatured}
            onChange={(e) => setIsFeatured(e.target.checked)}
            data-testid="admin-article-featured-checkbox"
            className="w-4 h-4 accent-[#FF5A4F]"
          />
          <span className="text-sm text-[#1C1917] flex items-center gap-1.5">
            <Star size={14} className={isFeatured ? "fill-[#F6A041] text-[#F6A041]" : "text-[#57534E]"} />
            Feature this article at the top of the grid
          </span>
        </label>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-article-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#FF5A4F] text-white px-6 py-3 text-sm font-medium hover:bg-[#e8463c] transition disabled:opacity-60"
          >
            {saving ? (
              <>
                <Loader2 className="animate-spin" size={16} /> Saving…
              </>
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Publish Article"
            )}
          </button>
          <Link
            to="/admin"
            data-testid="admin-article-cancel"
            className="text-sm text-[#57534E] hover:text-[#1C1917] transition"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
